import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  InputLabel,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Payment, Production, Staff } from "@/graphql/types";
import { ProductionSettingCallbacks, SetStaffsInput } from ".";
import SetupStaffs from "./setupStaffs";
import Button from "../common/button/button";
import { Device } from "@/lib/ts/device";
import SetupPayment from "./setupPayment";
import { useSnackBar } from "@/lib/ts/hooks/useSnackBar";
import CopyToClipboard from "react-copy-to-clipboard";
import SelectItemV2 from "../common/input/selectItemV2";

type Props = {
  productionList: Production[];
  payments: Payment[];
  callbacks: ProductionSettingCallbacks;
  device?: Device | undefined;
};

type ShowModal = "" | "setupStaffs" | "setupPayment";

const ProductionSetting = (props: Props) => {

  const [production, setProduction] = useState(
    undefined as Production | undefined,
  );
  const [staffs, setStaffs] = useState([] as Staff[]);

  const [showModal, setShowModal] = useState("" as ShowModal);

  const snackBar = useSnackBar({
    mutationSuccessMessage: "コピーしました。"
  })

  const onProductionChange = useCallback(
    async (production: Production) => {
      setProduction(production);
      const staffs = await props.callbacks.getStaffsOfProductionCallback(
        production.id,
      );
      setStaffs(staffs);
    },
    [props],
  );

  const save = useCallback(
    async (input: SetStaffsInput) => {
      snackBar.loadStart()
      await props.callbacks.saveStaffs(input);
      await onProductionChange(production!);
      snackBar.onQuerySuccess()
      setShowModal("");
    },
    [onProductionChange, production, props.callbacks, snackBar],
  );

  const productionReservationURL = useMemo(() => {
    const URL = `${process.env.REACT_APP_DOMAIN}/reservation/createReservation/${production?.id}`
    return (
      <div className="production-url">
        <div className="production-url-header">予約URL</div>
        <div className="production-url-body">
          <CopyToClipboard
            text={URL}
            onCopy={snackBar.onMutationSuccess}
          >
            <div className="url">{URL}</div>
          </CopyToClipboard>
        </div>
      </div>
    )
  }, [production?.id, snackBar.onMutationSuccess])

  const selectedProductionIndex = useMemo(() => {
    if(production && production.id) {
      return props.productionList.findIndex(p => production.id)
    }
    return -1
  }, [production, props.productionList])

  const components = useMemo(() => {
    return (
      <ProductionSettingStyle>
        <Accordion sx={{ borderRadius: "5px" }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
          >
            <Typography>公演設定</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div className="select-production">
              <InputLabel>公演選択</InputLabel>
              <SelectItemV2 
                items={props.productionList}
                selectedIndex={selectedProductionIndex}
                changeIndex={(item, index) => { onProductionChange(item) }}
                width={250}
              />
            </div>
            {production ? productionReservationURL : <></>}
            {
              production ? (
                <>
                  <div className="button">
                    <Button
                      label="スタッフ・出演者設定"
                      buttonType="powerful-red"
                      onClick={() => {
                        setShowModal("setupStaffs");
                      }}
                      disabled={production === undefined}
                      width={250}
                    />
                  </div>
                  <div className="button">
                    <Button
                      label="支払方法設定"
                      buttonType="powerful-red"
                      onClick={() => {
                        setShowModal("setupPayment");
                      }}
                      disabled={production === undefined}
                      width={250}
                    />
                  </div>
                </>
              ) : <></>
            }
            
          </AccordionDetails>
        </Accordion>
        {showModal === "setupStaffs" ? (
          <SetupStaffs
            existingStaffs={staffs}
            onClose={() => {
              setShowModal("");
            }}
            productionId={production ? production.id : -1}
            save={save}
            device={props.device}
          />
        ) : (
          <></>
        )}
        {
          showModal === "setupPayment" ? (
            <SetupPayment 
              getPaymentsOfProduction={props.callbacks.getPaymentsOfProductionCallback}
              updatePayments={props.callbacks.updatePaymentsCallback}
              productionId={production?.id || -1}
              onClose={() => {setShowModal("")}}
              device={props.device || "PC"}
            />
          ) : (
            <></>
          )
        }
        {snackBar.component}
      </ProductionSettingStyle>
    );
  }, [onProductionChange, production, productionReservationURL, props.callbacks.getPaymentsOfProductionCallback, props.callbacks.updatePaymentsCallback, props.device, props.productionList, save, selectedProductionIndex, showModal, snackBar.component, staffs]);
  return components;
};

export default ProductionSetting;

const ProductionSettingStyle = styled.div`
  .select-production {
    width: 250px;
    margin: auto;
  }
  .button {
    margin: 10px auto 10px auto;
  }
  .production-url {
    width: 300px;
    text-align: center;
    margin: auto;
    .production-url-header {
      margin: auto;
    }
    .production-url-body {
      width: 300px;
      overflow-wrap: normal;
      word-wrap: break-word;
      .url {
        cursor: pointer;
        border-style: solid;
        margin: 0 auto 20px auto;
        padding: 10px 10px 10px 10px;
        &:hover {
          background-color: black;
          color: white;
        }
      }
    }
  }
`;
